import React from 'react'
import { Modal, Form, Input, Button } from 'antd'
import { useMaiaT } from '$app/i18n/useMaiaT';
import {
  createPhoneBlurHandler,
  getPhoneFieldRules,
  getPhonePlaceholder,
  normalizePhoneFields,
} from '../../../../utils/phoneValidation'

const formItemLayout = {
  labelCol: {
    sm: { span: 6 },
  },
  wrapperCol: {
    sm: { span: 18 },
  },
  layout: 'vertical'
}

const CreateEditModalForm = Form.create({ name: 'form_in_modal' })(
  class extends React.Component {
    state = {
      submitting: false,
      deleting: false,
    }

    onOk = () => {
      const { form, onCreate, editing, onEdit, segment } = this.props
      if (this.state.submitting || this.state.deleting) {
        return;
      }

      form.validateFields(async (err, values) => {
        if (err) {
          return;
        }

        this.setState({ submitting: true })
        try {
          const normalizedValues = normalizePhoneFields(values, segment, ['phoneNumber'])
          if (editing) {
            normalizedValues.userId = editing.userId
            await onEdit(normalizedValues)
          } else {
            await onCreate(normalizedValues)
          }
          form.resetFields()
        } finally {
          this.setState({ submitting: false })
        }
      })
    }

    onCancel = () => {
      const { onCancel, form } = this.props
      if (this.state.submitting || this.state.deleting) {
        return;
      }
      form.resetFields()
      onCancel()
    }

    onDelete = async () => {
      const { form, onDelete, editing } = this.props
      if (this.state.submitting || this.state.deleting || !editing) {
        return;
      }

      this.setState({ deleting: true })
      try {
        await onDelete(editing.userId)
        form.resetFields()
      } finally {
        this.setState({ deleting: false })
      }
    }

    render() {
      const { visible, form, editing, segment, t } = this.props
      const { getFieldDecorator } = form
      const { submitting, deleting } = this.state
      const busy = submitting || deleting
      const phonePlaceholder = getPhonePlaceholder(segment, (key, fallback) => fallback)

      const footer = [
        <Button key='cancel' onClick={this.onCancel} disabled={busy}>
          {t('maia.userAccounts.createEditModal.cancelCta', 'Cancel')}
        </Button>,
        <Button key='submit' type='primary' onClick={this.onOk} loading={submitting} disabled={busy}>
          {editing ? t('maia.userAccounts.createEditModal.saveCta', 'Save') : t('maia.userAccounts.createEditModal.createCta', 'Create')}
        </Button>,
      ]

      if (editing) {
        footer.unshift(
          <Button key='delete' type='danger' onClick={this.onDelete} className='pull-left' loading={deleting} disabled={submitting}>
            {t('maia.userAccounts.createEditModal.deleteCta', 'Delete')}
          </Button>
        )
      }

      return (
        <Modal
          visible={visible}
          title={editing ? <b>{t('maia.userAccounts.createEditModal.editTitle', 'Edit account')} - {editing.email}</b> : <b>{t('maia.userAccounts.createEditModal.createTitle', 'Create account')}</b>}
          onCancel={this.onCancel}
          onOk={this.onOk}
          maskClosable={!busy}
          closable={!busy}
          footer={footer}
        >
          <Form {...formItemLayout}>
            {
              !editing &&
              <Form.Item label={t('maia.userAccounts.createEditModal.emailLabel', 'Email')}>
                {getFieldDecorator('email', {
                  rules: [{
                    required: true,
                    message: t('maia.userAccounts.createEditModal.emailRequiredHelp', 'Please input an email address')
                  }, {
                    type: 'email',
                    message: t('maia.userAccounts.createEditModal.emailInvalidHelp', 'The input is not valid email!'),
                  }],
                })(<Input type='email' />)}
              </Form.Item>
            }
            <Form.Item label={t('maia.userAccounts.createEditModal.nameLabel', 'Name')}>
              {getFieldDecorator('name', { initialValue: editing && editing.name })(<Input type='text' />)}
            </Form.Item>
            <Form.Item label={t('maia.userAccounts.createEditModal.phoneNumberLabel', 'Phone Number')}>
              {getFieldDecorator('phoneNumber', {
                initialValue: editing && editing.userMetadata.phoneNumber,
                rules: getPhoneFieldRules({
                  form,
                  segment,
                  t: (key, fallback) => fallback,
                }),
              })(
                <Input
                  type='tel'
                  placeholder={phonePlaceholder}
                  onBlur={createPhoneBlurHandler({ form, fieldName: 'phoneNumber', segment })}
                />
              )}
            </Form.Item>
          </Form>
        </Modal>
      )
    }
  },
)

export default function CreateEditModal(props) {
  const t = useMaiaT();
  return <CreateEditModalForm {...props} t={t} />;
}
